import React, { useState } from 'react';

import { cssMap as unboundedCssMap } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import {
	type ActionItem,
	ActionName,
	Card,
	type ElementItem,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	SmartLinkTheme,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import type { LinkListComponentProps, LinkListVariantProps } from './types';
import { useLinkListMarkErrored } from './use-link-list-mark-errored';

const styles = cssMap({
	root: {
		borderLeftColor: token('color.border'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderRightColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		position: 'relative',
	},
	linkWrapperUnresolved: {
		minHeight: '52px',
	},
	unresolvedSkeletonOverlay: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.medium'),
		zIndex: 1,
	},
	borderTop: {
		borderTopColor: token('color.border'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
	},
	borderTopRounded: {
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	borderBottomRounded: {
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
	loadingContainer: {
		paddingTop: token('space.150'),
		paddingLeft: token('space.150'),
	},
	loadingSkeletonRows: {
		flex: '1',
		minWidth: '0px',
	},
});

const unboundedStyles = unboundedCssMap({
	linkWrapper: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-title-resolved-view"]': {
			display: 'flex',
			alignItems: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-links-container"]': {
			paddingTop: token('space.100'),
			paddingRight: token('space.150'),
			paddingBottom: token('space.075'),
			paddingLeft: token('space.150'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& a[data-smart-element="Title"]': {
			font: token('font.body.small'),
			fontWeight: token('font.weight.semibold'),
			color: token('color.text'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-element-group"]': {
			maxWidth: '375px',
			gap: token('space.0'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& div[data-testid="smart-block-title-resolved-view"] .actions-button-group': {
			marginLeft: 'auto',
			alignSelf: 'center',
			display: 'flex',
			alignItems: 'center',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="CreatedBy"]::before': {
			content: '•',
			marginRight: token('space.100'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="ModifiedOn"]::before': {
			content: 'none',
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="Priority"]': {
			marginLeft: '64px',
			marginRight: '8px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'span[data-smart-element="Priority"] > span[data-testid="smart-element-badge-label"]': {
			display: 'none',
		},
	},
});

const actions: ActionItem[] = [
	{
		name: ActionName.PreviewAction,
		hideContent: true,
	},
];
const metadata: ElementItem[] = [
	{ name: ElementName.State },
	{ name: ElementName.AuthorGroup },
	{ name: ElementName.ModifiedOn },
	{ name: ElementName.Priority },
];

const LinkListFullComponent = ({
	url,
	idx,
	borderTopRounded,
	borderBottomRounded,
	onError,
}: LinkListComponentProps) => {
	const [resolved, setResolved] = useState(false);
	const handleResolve = () => setResolved(true);

	const handleError = () => {
		onError();
	};

	return (
		<Box
			xcss={cx(
				styles.root,
				unboundedStyles.linkWrapper,
				styles.borderTop,
				borderTopRounded && styles.borderTopRounded,
				borderBottomRounded && styles.borderBottomRounded,
				!resolved && styles.linkWrapperUnresolved,
			)}
			testId={`link-list-full-item-${idx}${borderTopRounded ? '-top-rounded' : ''}${
				borderBottomRounded ? '-bottom-rounded' : ''
			}`}
		>
			<Card
				appearance="block"
				url={url}
				ui={{
					theme: SmartLinkTheme.Black,
					clickableContainer: true,
					hideBackground: true,
					hideElevation: true,
					hidePadding: true,
					size: SmartLinkSize.Medium,
					removeBlockRestriction: true,
				}}
				onError={handleError}
				onResolve={handleResolve}
			>
				<TitleBlock
					maxLines={1}
					actions={actions}
					showActionOnHover
					metadata={metadata}
					subtitle={[
						{ name: ElementName.Provider, hideIcon: true },
						{ name: ElementName.CreatedBy },
					]}
					position={SmartLinkPosition.Center}
				/>
			</Card>
			{!resolved && (
				<Box
					xcss={cx(styles.unresolvedSkeletonOverlay, styles.loadingContainer)}
					testId="unresolved-loading-skeleton"
				>
					<Inline space="space.100" alignBlock="center">
						<Skeleton width="16px" height="16px" borderRadius={token('radius.small')} />
						<Stack xcss={styles.loadingSkeletonRows} space="space.100">
							<Skeleton width="40%" height="12px" borderRadius={token('radius.small')} />
							<Skeleton width="70%" height="12px" borderRadius={token('radius.small')} />
						</Stack>
					</Inline>
				</Box>
			)}
		</Box>
	);
};

export const LinkListFull = ({
	visibleLinks,
	borderBottomRounded,
}: LinkListVariantProps): React.JSX.Element => {
	const { erroredIndices, markErrored } = useLinkListMarkErrored();
	const visibleIndices = visibleLinks.map((_, i) => i).filter((i) => !erroredIndices.has(i));
	const firstVisibleIdx = visibleIndices[0] ?? -1;
	const lastVisibleIdx = visibleIndices[visibleIndices.length - 1] ?? -1;

	return (
		<Stack space="space.0">
			{visibleIndices.map((url, idx) => (
				<LinkListFullComponent
					key={`${visibleLinks[idx]}-${idx}`}
					url={visibleLinks[idx]}
					idx={idx}
					borderTopRounded={idx === firstVisibleIdx}
					borderBottomRounded={idx === lastVisibleIdx && borderBottomRounded}
					onError={() => markErrored(idx)}
				/>
			))}
		</Stack>
	);
};
